import { useState, useRef } from 'react'
import Navbar from '../components/layout/Navbar'
import { importApi } from '../api/importer'

const QUICK_SEARCHES = [
  'Restaurants Phoenix AZ','Hair salons Scottsdale AZ','Auto repair shops Phoenix AZ',
  'Dental offices Phoenix AZ','Med spas Scottsdale AZ','Contractors Phoenix AZ',
  'Chiropractors Phoenix AZ','Veterinarians Phoenix AZ','Gyms and fitness Phoenix AZ','Law firms Phoenix AZ',
]
const MAX_OPTIONS = [10, 20, 40, 60]

function ratingColor(r) {
  if (!r) return '#888'
  if (r >= 4.3) return '#639922'
  if (r >= 3.0) return '#BA7517'
  return '#E24B4A'
}

function scoreColor(s) {
  if (s >= 75) return '#639922'
  if (s >= 50) return '#BA7517'
  return '#E24B4A'
}

function actionIcon(action) {
  switch (action) {
    case 'imported': return '✅'
    case 'updated':  return '🔄'
    case 'skipped':  return '⏭️'
    case 'failed':   return '❌'
    default:         return '•'
  }
}

function actionStyle(action) {
  const colors = { imported: '#0F6E56', updated: '#185FA5', skipped: '#888', failed: '#E24B4A' }
  return { color: colors[action] || '#333', fontWeight: 500, fontSize: 12 }
}

export default function ImportPage() {
  const [csvFile, setCsvFile] = useState(null)
  const [csvMode, setCsvMode] = useState('idle') // idle | verifying | verified | importing | done
  const [csvRows, setCsvRows] = useState([])
  const [selectedRows, setSelectedRows] = useState(new Set())
  const [columnMapping, setColumnMapping] = useState({})
  const [csvResult, setCsvResult] = useState(null)
  const [csvError, setCsvError] = useState(null)
  const [logOpen, setLogOpen] = useState(false)
  const fileInputRef = useRef(null)

  const [query, setQuery] = useState('')
  const [maxResults, setMaxResults] = useState(20)
  const [results, setResults] = useState([])
  const [selected, setSelected] = useState(new Set())
  const [searching, setSearching] = useState(false)
  const [importing, setImporting] = useState(false)
  const [searchError, setSearchError] = useState(null)
  const [importResult, setImportResult] = useState(null)

  const handleFileChange = (e) => {
    const f = e.target.files[0]
    if (f) { setCsvFile(f); resetCSV() }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    const f = e.dataTransfer.files[0]
    if (f && f.name.endsWith('.csv')) { setCsvFile(f); resetCSV() }
  }

  const resetCSV = () => {
    setCsvMode('idle'); setCsvRows([]); setSelectedRows(new Set())
    setCsvResult(null); setCsvError(null); setColumnMapping({}); setLogOpen(false)
  }

  const handleVerify = async () => {
    if (!csvFile) return
    setCsvMode('verifying')
    setCsvError(null)
    try {
      const res = await importApi.verifyCSV(csvFile)
      if (res.data.errors.length > 0) {
        setCsvError(res.data.errors[0])
        setCsvMode('idle')
        return
      }
      setCsvRows(res.data.rows)
      setColumnMapping(res.data.column_mapping)
      setSelectedRows(new Set(res.data.rows.map((_, i) => i)))
      setCsvMode('verified')
    } catch (e) {
      setCsvError(e.response?.data?.detail || 'Verification failed.')
      setCsvMode('idle')
    }
  }

  const handleImportVerified = async () => {
    const toImport = csvRows.filter((_, i) => selectedRows.has(i))
    if (toImport.length === 0) return
    setCsvMode('importing')
    try {
      const res = await importApi.importVerified(toImport)
      setCsvResult(res.data)
      setCsvMode('done')
    } catch (e) {
      setCsvError(e.response?.data?.detail || 'Import failed.')
      setCsvMode('verified')
    }
  }

  const toggleRow = (i) => {
    setSelectedRows(prev => {
      const next = new Set(prev)
      next.has(i) ? next.delete(i) : next.add(i)
      return next
    })
  }

  const toggleAllRows = () => {
    if (selectedRows.size === csvRows.length) setSelectedRows(new Set())
    else setSelectedRows(new Set(csvRows.map((_, i) => i)))
  }

  const handleSearch = async (q = query) => {
    if (!q.trim()) return
    setSearching(true); setSearchError(null); setResults([]); setSelected(new Set()); setImportResult(null)
    try {
      const res = await importApi.search(q.trim(), maxResults)
      setResults(res.data)
      if (res.data.length === 0) setSearchError('No businesses found.')
    } catch (e) {
      setSearchError(e.response?.data?.detail || 'Search failed.')
    } finally { setSearching(false) }
  }

  const handleQuickSearch = (q) => { setQuery(q); handleSearch(q) }

  const toggleSelect = (placeId) => {
    setSelected(prev => { const next = new Set(prev); next.has(placeId) ? next.delete(placeId) : next.add(placeId); return next })
  }

  const toggleAll = () => {
    if (selected.size === results.length) setSelected(new Set())
    else setSelected(new Set(results.map(r => r.place_id)))
  }

  const handleImport = async () => {
    if (selected.size === 0) return
    setImporting(true); setImportResult(null)
    try {
      const res = await importApi.importPlaces([...selected], query)
      setImportResult(res.data); setSelected(new Set())
    } catch (e) {
      setImportResult({ error: e.response?.data?.detail || 'Import failed.' })
    } finally { setImporting(false) }
  }

  const allSelected = results.length > 0 && selected.size === results.length
  const foundCount = csvRows.filter(r => r.google_found).length
  const notFoundCount = csvRows.filter(r => r.google_found === false).length

  return (
    <div style={styles.root}>
      <Navbar />
      <main style={styles.main}>

        {/* CSV Import */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>CSV Import</h2>
          <p style={styles.sectionSub}>Upload a CSV from any CRM or spreadsheet. PE will verify each business on Google and show you their rating before you import.</p>

          {csvMode === 'idle' && (
            <>
              <div
                style={{ ...styles.dropzone, ...(csvFile ? styles.dropzoneActive : {}) }}
                onDrop={handleDrop}
                onDragOver={e => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
              >
                <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFileChange} />
                {csvFile ? (
                  <div style={styles.dropzoneContent}>
                    <div style={styles.fileName}>📄 {csvFile.name}</div>
                    <div style={styles.fileSize}>{(csvFile.size / 1024).toFixed(1)} KB — click to change</div>
                  </div>
                ) : (
                  <div style={styles.dropzoneContent}>
                    <div style={styles.dropIcon}>📂</div>
                    <div style={styles.dropText}>Drop your CSV here or click to browse</div>
                    <div style={styles.dropSub}>Supports any CRM export — company, contact, email, phone, website, and more</div>
                  </div>
                )}
              </div>
              {csvFile && (
                <div style={styles.csvActions}>
                  <button style={styles.cancelFileBtn} onClick={() => { setCsvFile(null); resetCSV() }}>Remove</button>
                  <button style={styles.verifyBtn} onClick={handleVerify}>
                    🔍 Verify with Google Places
                  </button>
                  <button style={styles.importCsvBtn} onClick={async () => {
                    setCsvMode('importing')
                    try {
                      const res = await importApi.importCSV(csvFile)
                      setCsvResult(res.data); setCsvMode('done')
                    } catch(e) { setCsvError(e.response?.data?.detail || 'Import failed.'); setCsvMode('idle') }
                  }}>
                    Import without verifying
                  </button>
                </div>
              )}
              {csvError && <div style={styles.errorBanner}>{csvError}</div>}
            </>
          )}

          {csvMode === 'verifying' && (
            <div style={styles.verifyingState}>
              <div style={styles.spinnerText}>🔍 Looking up each business on Google Places...</div>
              <div style={styles.spinnerSub}>This may take a moment for large files</div>
            </div>
          )}

          {csvMode === 'verified' && (
            <>
              <div style={styles.verifyStats}>
                <div style={styles.verifyStat}>
                  <span style={{ ...styles.verifyStatVal, color: '#0F6E56' }}>{foundCount}</span>
                  <span style={styles.verifyStatLabel}>Found on Google</span>
                </div>
                <div style={styles.verifyStat}>
                  <span style={{ ...styles.verifyStatVal, color: '#888' }}>{notFoundCount}</span>
                  <span style={styles.verifyStatLabel}>Not found</span>
                </div>
                <div style={styles.verifyStat}>
                  <span style={styles.verifyStatVal}>{csvRows.length}</span>
                  <span style={styles.verifyStatLabel}>Total rows</span>
                </div>
              </div>

              <div style={styles.mappingInfo}>
                <span style={styles.mappingLabel}>Detected columns:</span>
                {Object.entries(columnMapping).map(([field, col]) => (
                  <span key={field} style={styles.mappingChip}>
                    <span style={styles.mappingField}>{field}</span> ← {col}
                  </span>
                ))}
              </div>

              <div style={styles.verifyToolbar}>
                <label style={styles.selectAllLabel}>
                  <input type="checkbox" checked={selectedRows.size === csvRows.length} onChange={toggleAllRows} style={{ marginRight: 6 }} />
                  {selectedRows.size === csvRows.length ? 'Deselect all' : `Select all (${csvRows.length})`}
                </label>
                <span style={styles.selectedCount}>{selectedRows.size} selected</span>
                <button style={styles.importCsvBtn} onClick={handleImportVerified} disabled={selectedRows.size === 0}>
                  Import {selectedRows.size} selected
                </button>
              </div>

              <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.th, width: 40 }}></th>
                      <th style={styles.th}>Company</th>
                      <th style={styles.th}>Contact</th>
                      <th style={styles.th}>Email</th>
                      <th style={styles.th}>Phone</th>
                      <th style={{ ...styles.th, width: 80 }}>Google</th>
                      <th style={{ ...styles.th, width: 80 }}>Rating</th>
                      <th style={{ ...styles.th, width: 80 }}>Reviews</th>
                      <th style={{ ...styles.th, width: 70 }}>Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvRows.map((row, i) => (
                      <tr key={i} style={{ ...styles.row, background: selectedRows.has(i) ? '#f0f7ff' : 'transparent', cursor: 'pointer' }} onClick={() => toggleRow(i)}>
                        <td style={styles.td}><input type="checkbox" checked={selectedRows.has(i)} onChange={() => toggleRow(i)} onClick={e => e.stopPropagation()} /></td>
                        <td style={{ ...styles.td, fontWeight: 500 }}>{row.company}</td>
                        <td style={styles.td}>{row.contact || '—'}</td>
                        <td style={{ ...styles.td, color: '#185FA5', fontSize: 12 }}>{row.email || '—'}</td>
                        <td style={{ ...styles.td, fontSize: 12 }}>{row.phone || '—'}</td>
                        <td style={styles.td}>
                          {row.google_found === true
                            ? <span style={styles.foundBadge}>✓ Found</span>
                            : row.google_found === false
                            ? <span style={styles.notFoundBadge}>✗ Not found</span>
                            : '—'}
                        </td>
                        <td style={styles.td}>
                          {row.google_rating
                            ? <span style={{ color: ratingColor(row.google_rating), fontWeight: 500 }}>★ {row.google_rating}</span>
                            : '—'}
                        </td>
                        <td style={{ ...styles.td, color: '#666' }}>{row.google_reviews ? row.google_reviews.toLocaleString() : '—'}</td>
                        <td style={styles.td}>
                          {row.score > 0
                            ? <span style={{ color: scoreColor(row.score), fontWeight: 500 }}>{row.score}</span>
                            : <span style={{ color: '#ccc' }}>0</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: 8 }}>
                <button style={styles.cancelFileBtn} onClick={() => { setCsvFile(null); resetCSV() }}>Start over</button>
              </div>
            </>
          )}

          {csvMode === 'importing' && (
            <div style={styles.verifyingState}>
              <div style={styles.spinnerText}>📥 Importing prospects...</div>
            </div>
          )}

          {csvMode === 'done' && csvResult && (
            <div style={styles.csvResult}>

              {/* Summary numbers */}
              <div style={styles.csvStats}>
                <div style={styles.csvStat}>
                  <span style={{ ...styles.csvStatVal, color: '#0F6E56' }}>{csvResult.imported}</span>
                  <span style={styles.csvStatLabel}>Imported</span>
                </div>
                <div style={styles.csvStat}>
                  <span style={{ ...styles.csvStatVal, color: '#185FA5' }}>{csvResult.updated}</span>
                  <span style={styles.csvStatLabel}>Updated</span>
                </div>
                <div style={styles.csvStat}>
                  <span style={{ ...styles.csvStatVal, color: '#888' }}>{csvResult.skipped}</span>
                  <span style={styles.csvStatLabel}>Skipped</span>
                </div>
                <div style={styles.csvStat}>
                  <span style={{ ...styles.csvStatVal, color: '#E24B4A' }}>{csvResult.failed}</span>
                  <span style={styles.csvStatLabel}>Failed</span>
                </div>
              </div>

              {/* Filter notes (chains skipped, review cap, etc.) */}
              {csvResult.notes && csvResult.notes.length > 0 && (
                <div style={styles.csvNotes}>
                  {csvResult.notes.map((n, i) => (
                    <div key={i} style={styles.csvNoteRow}>ℹ️ {n}</div>
                  ))}
                </div>
              )}

              {/* Hard errors */}
              {csvResult.errors && csvResult.errors.length > 0 && (
                <div style={styles.csvErrors}>
                  {csvResult.errors.slice(0, 5).map((e, i) => (
                    <div key={i} style={styles.csvErrorRow}>⚠️ {e}</div>
                  ))}
                  {csvResult.errors.length > 5 && (
                    <div style={styles.csvErrorRow}>...and {csvResult.errors.length - 5} more</div>
                  )}
                </div>
              )}

              {/* Expandable row-by-row log */}
              {csvResult.detail_log && csvResult.detail_log.length > 0 && (
                <div style={styles.logSection}>
                  <button style={styles.logToggle} onClick={() => setLogOpen(prev => !prev)}>
                    {logOpen ? '▲ Hide' : '▼ Show'} row details ({csvResult.detail_log.length} rows)
                  </button>
                  {logOpen && (
                    <div style={styles.logTable}>
                      <div style={styles.logHeader}>
                        <span style={{ flex: 2 }}>Business</span>
                        <span style={{ flex: 1 }}>Result</span>
                        <span style={{ flex: 3 }}>Reason</span>
                      </div>
                      {csvResult.detail_log.map((row, i) => (
                        <div key={i} style={{ ...styles.logRow, background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)' }}>
                          <span style={{ flex: 2, fontWeight: 500 }}>{row.company}</span>
                          <span style={{ flex: 1, ...actionStyle(row.action) }}>
                            {actionIcon(row.action)} {row.action}
                          </span>
                          <span style={{ flex: 3, color: '#666', fontSize: 12 }}>
                            {row.reason || '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <button
                style={{ ...styles.cancelFileBtn, marginTop: 16 }}
                onClick={() => { setCsvFile(null); resetCSV() }}
              >
                Import another file
              </button>
            </div>
          )}
        </div>

        {/* Divider */}
        <div style={styles.divider}><span style={styles.dividerLabel}>or import from Google Places</span></div>

        {/* Google Places */}
        <div style={styles.searchCard}>
          <div style={styles.searchRow}>
            <input type="text" placeholder="e.g. Restaurants Phoenix AZ" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} style={styles.searchInput} />
            <select value={maxResults} onChange={e => setMaxResults(Number(e.target.value))} style={styles.maxSelect}>
              {MAX_OPTIONS.map(n => <option key={n} value={n}>Up to {n} results</option>)}
            </select>
            <button onClick={() => handleSearch()} disabled={searching || !query.trim()} style={styles.searchBtn}>
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>
          <div style={styles.quickRow}>
            <span style={styles.quickLabel}>Quick:</span>
            {QUICK_SEARCHES.map(q => <button key={q} style={styles.quickChip} onClick={() => handleQuickSearch(q)} disabled={searching}>{q}</button>)}
          </div>
        </div>

        {importResult && !importResult.error && (
          <div style={styles.successBanner}><strong>Import complete —</strong> {importResult.imported} imported, {importResult.updated} updated, {importResult.failed} failed.</div>
        )}
        {importResult?.error && <div style={styles.errorBanner}>{importResult.error}</div>}
        {searchError && !searching && <div style={styles.errorBanner}>{searchError}</div>}

        {results.length > 0 && (
          <div style={styles.resultsCard}>
            <div style={styles.resultsToolbar}>
              <label style={styles.selectAllLabel}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ marginRight: 6 }} />
                {allSelected ? 'Deselect all' : `Select all (${results.length})`}
              </label>
              <span style={styles.selectedCount}>{selected.size} of {results.length} selected</span>
              <button style={{ ...styles.importBtn, opacity: selected.size === 0 || importing ? 0.5 : 1 }} onClick={handleImport} disabled={selected.size === 0 || importing}>
                {importing ? 'Importing...' : `Import ${selected.size > 0 ? selected.size : ''} selected`}
              </button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, width: 40 }}></th>
                    <th style={styles.th}>Business</th>
                    <th style={styles.th}>Address</th>
                    <th style={{ ...styles.th, width: 80 }}>Rating</th>
                    <th style={{ ...styles.th, width: 90 }}>Reviews</th>
                    <th style={styles.th}>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map(r => {
                    const isSel = selected.has(r.place_id)
                    return (
                      <tr key={r.place_id} style={{ ...styles.row, background: isSel ? '#f0f7ff' : 'transparent', cursor: 'pointer' }} onClick={() => toggleSelect(r.place_id)}>
                        <td style={styles.td}><input type="checkbox" checked={isSel} onChange={() => toggleSelect(r.place_id)} onClick={e => e.stopPropagation()} /></td>
                        <td style={{ ...styles.td, fontWeight: 500 }}>{r.company}</td>
                        <td style={{ ...styles.td, color: '#666', fontSize: 12 }}>{r.address}</td>
                        <td style={styles.td}>{r.rating ? <span style={{ color: ratingColor(r.rating), fontWeight: 500 }}>★ {r.rating}</span> : '—'}</td>
                        <td style={{ ...styles.td, color: '#666' }}>{r.review_count.toLocaleString()}</td>
                        <td style={{ ...styles.td, fontSize: 12, color: '#888' }}>{r.types.slice(0, 2).join(', ').replace(/_/g, ' ')}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!searching && results.length === 0 && !searchError && !csvFile && (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>🔍</div>
            <p style={styles.emptyTitle}>Search for businesses or upload a CSV to get started</p>
          </div>
        )}
      </main>
    </div>
  )
}

const styles = {
  root: { minHeight: '100vh', background: '#f5f5f3' },
  main: { maxWidth: 1100, margin: '0 auto', padding: '1.5rem' },
  section: { background: '#fff', border: '0.5px solid #e0dfd8', borderRadius: 12, padding: '1.5rem', marginBottom: '1rem' },
  sectionTitle: { fontSize: 18, fontWeight: 500, color: '#1a1a1a', marginBottom: 4 },
  sectionSub: { fontSize: 13, color: '#888', marginBottom: '1.25rem' },
  dropzone: { border: '2px dashed #e0dfd8', borderRadius: 12, padding: '2rem', textAlign: 'center', cursor: 'pointer' },
  dropzoneActive: { borderColor: '#185FA5', background: '#f0f7ff' },
  dropzoneContent: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 },
  dropIcon: { fontSize: 32, marginBottom: 4 },
  dropText: { fontSize: 15, fontWeight: 500, color: '#1a1a1a' },
  dropSub: { fontSize: 13, color: '#888' },
  fileName: { fontSize: 15, fontWeight: 500, color: '#185FA5' },
  fileSize: { fontSize: 12, color: '#888' },
  csvActions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: '1rem', flexWrap: 'wrap' },
  cancelFileBtn: { background: 'none', border: '0.5px solid #ccc', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer', color: '#444' },
  verifyBtn: { background: '#E6F1FB', color: '#185FA5', border: '0.5px solid #B5D4F4', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  importCsvBtn: { background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  verifyingState: { textAlign: 'center', padding: '2rem' },
  spinnerText: { fontSize: 15, fontWeight: 500, color: '#1a1a1a', marginBottom: 6 },
  spinnerSub: { fontSize: 13, color: '#888' },
  verifyStats: { display: 'flex', gap: 24, marginBottom: '1rem' },
  verifyStat: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  verifyStatVal: { fontSize: 28, fontWeight: 500, color: '#1a1a1a' },
  verifyStatLabel: { fontSize: 12, color: '#888' },
  mappingInfo: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: '1rem', padding: '10px 14px', background: '#f5f5f3', borderRadius: 8 },
  mappingLabel: { fontSize: 12, color: '#888', marginRight: 4 },
  mappingChip: { fontSize: 11, background: '#fff', border: '0.5px solid #e0dfd8', borderRadius: 6, padding: '2px 8px' },
  mappingField: { color: '#185FA5', fontWeight: 500 },
  verifyToolbar: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', marginBottom: 6 },
  selectAllLabel: { fontSize: 13, color: '#444', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  selectedCount: { fontSize: 12, color: '#888', flex: 1 },
  foundBadge: { fontSize: 11, background: '#EAF3DE', color: '#27500A', padding: '2px 6px', borderRadius: 20 },
  notFoundBadge: { fontSize: 11, background: '#F1EFE8', color: '#5F5E5A', padding: '2px 6px', borderRadius: 20 },
  csvResult: { marginTop: '1rem' },
  csvStats: { display: 'flex', gap: 24, marginBottom: '1rem' },
  csvStat: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  csvStatVal: { fontSize: 28, fontWeight: 500 },
  csvStatLabel: { fontSize: 12, color: '#888' },
  csvErrors: { background: '#FCEBEB', borderRadius: 8, padding: '10px 14px', marginBottom: '1rem' },
  csvErrorRow: { fontSize: 12, color: '#791F1F', marginBottom: 2 },
  csvNotes: { background: '#f0f7ff', borderRadius: 8, padding: '10px 14px', marginBottom: '1rem' },
  csvNoteRow: { fontSize: 12, color: '#185FA5', marginBottom: 2 },
  logSection: { marginTop: 16, borderTop: '1px solid #eee', paddingTop: 12 },
  logToggle: { background: 'none', border: '0.5px solid #ddd', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: '#555', marginBottom: 10 },
  logTable: { fontSize: 13, borderRadius: 6, border: '1px solid #eee', overflow: 'hidden' },
  logHeader: { display: 'flex', gap: 8, padding: '8px 12px', background: '#f5f5f5', fontWeight: 600, fontSize: 12, color: '#555', borderBottom: '1px solid #eee' },
  logRow: { display: 'flex', gap: 8, padding: '7px 12px', borderBottom: '1px solid #f0f0f0', alignItems: 'center' },
  errorBanner: { background: '#FCEBEB', color: '#791F1F', border: '0.5px solid #F09595', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: '1rem', marginTop: '1rem' },
  successBanner: { background: '#EAF3DE', color: '#27500A', border: '0.5px solid #C0DD97', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: '1rem' },
  divider: { display: 'flex', alignItems: 'center', gap: 12, margin: '1.5rem 0' },
  dividerLabel: { fontSize: 12, color: '#888', whiteSpace: 'nowrap', background: '#f5f5f3', padding: '0 12px' },
  searchCard: { background: '#fff', border: '0.5px solid #e0dfd8', borderRadius: 12, padding: '1.25rem', marginBottom: '1rem' },
  searchRow: { display: 'flex', gap: 10, marginBottom: '1rem' },
  searchInput: { flex: 1, padding: '9px 12px', fontSize: 14, border: '0.5px solid #ccc', borderRadius: 8, outline: 'none', fontFamily: 'inherit' },
  maxSelect: { padding: '9px 10px', fontSize: 13, border: '0.5px solid #ccc', borderRadius: 8, background: '#fff', fontFamily: 'inherit' },
  searchBtn: { background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 14, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' },
  quickRow: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' },
  quickLabel: { fontSize: 12, color: '#888', marginRight: 2 },
  quickChip: { background: '#f5f5f3', border: '0.5px solid #e0dfd8', borderRadius: 20, padding: '4px 12px', fontSize: 12, cursor: 'pointer', color: '#444', whiteSpace: 'nowrap' },
  resultsCard: { background: '#fff', border: '0.5px solid #e0dfd8', borderRadius: 12, overflow: 'hidden' },
  resultsToolbar: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '0.5px solid #e0dfd8', background: '#fafaf8' },
  importBtn: { background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 18px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', fontWeight: 500, fontSize: 11, color: '#888', padding: '8px 12px', borderBottom: '0.5px solid #e0dfd8' },
  td: { padding: '10px 12px', borderBottom: '0.5px solid #f0efe8', color: '#1a1a1a' },
  row: { transition: 'background 0.1s' },
  emptyState: { textAlign: 'center', padding: '4rem 2rem' },
  emptyIcon: { fontSize: 36, marginBottom: '1rem' },
  emptyTitle: { fontSize: 15, color: '#888' },
}